import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Mapa de rotas para permissões necessárias
const ROUTE_PERMISSIONS = {
  '/dashboard': 'can_view_dashboard',
  '/work-orders': 'can_assign_work_order',
  '/my-work-orders': 'can_view_my_os',
  '/departments': 'can_manage_departments',
  '/departments/queue': 'can_view_sector_queue',
  '/users': 'can_manage_users',
  '/materials': 'can_manage_materials',
  '/purchase-requests': 'can_view_purchase_requests',
  '/purchase-requests/': 'can_view_purchase_requests',
  '/stock-movements': 'can_manage_stock',
  '/bonus-rules': 'can_configure_bonus',
  '/redemptions': 'can_manage_redemptions',
  '/tech-dashboard': 'can_view_my_panel',
  '/coins': 'can_view_my_coins',
  '/patents': 'can_manage_patents',
  '/my-performance': 'can_view_my_panel',
  '/system-settings': 'can_manage_settings',
  '/roles': '_admin_only',
  '/job-roles': '_admin_only',
  '/work-schedule': 'can_assign_work_order',
  '/suppliers': 'can_manage_suppliers',
  '/material-categories': 'can_manage_materials',
  '/quotations': 'can_manage_quotations',
  '/purchase-orders': 'can_view_purchase_requests',
  '/purchase-dashboard': 'can_view_purchase_requests',
  '/stock-alerts': 'can_manage_stock',
  '/company-settings': 'can_manage_settings',
  '/areas': 'can_manage_departments',
  '/reports/validate': 'can_validate_report',
  '/calls/new': 'can_create_call',
  '/reports/new': 'can_create_report',
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading, permissions, tenantSlug, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Strip tenant prefix to check permission
  let path = location.pathname;
  const tenantPrefix = `/t/${tenantSlug}`;
  if (path.startsWith(tenantPrefix)) {
    path = path.substring(tenantPrefix.length) || '/dashboard';
  }

  // Check route-level permission
  let requiredPerm = ROUTE_PERMISSIONS[path];
  if (!requiredPerm) {
    const matchingRoute = Object.keys(ROUTE_PERMISSIONS).find(route =>
      path.startsWith(route + '/') || path === route
    );
    if (matchingRoute) requiredPerm = ROUTE_PERMISSIONS[matchingRoute];
  }

  if (requiredPerm) {
    const perms = permissions || user.permissions || {};
    const role = (user.role || '').toUpperCase();
    const isAdmin = role === 'ADMIN';

    const hasAccess = requiredPerm === '_admin_only' ? isAdmin : (isAdmin || perms[requiredPerm]);

    if (!hasAccess) {
      // Redirect to first accessible route instead of showing "Access Denied"
      const tp = tenantSlug ? `/t/${tenantSlug}` : '';
      // Try "Meu Painel" first
      if (hasPermission('can_view_my_panel')) {
        return <Navigate to={`${tp}/tech-dashboard`} replace />;
      }
      // Then dashboard
      if (hasPermission('can_view_dashboard')) {
        return <Navigate to={`${tp}/dashboard`} replace />;
      }
      // Then first available route
      for (const [route, perm] of Object.entries(ROUTE_PERMISSIONS)) {
        if (perm === '_admin_only') continue;
        if (hasPermission(perm)) {
          return <Navigate to={`${tp}${route}`} replace />;
        }
      }
      // Fallback to login
      return <Navigate to="/login" replace />;
    }
  }

  return children;
};

function AccessDenied() {
  const { tenantSlug } = useAuth();
  const dashPath = tenantSlug ? `/t/${tenantSlug}/dashboard` : '/login';
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md px-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Acesso Negado</h2>
        <p className="text-muted-foreground mb-4">Voce nao tem permissao para acessar esta pagina.</p>
        <a href={dashPath} className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
          Voltar ao inicio
        </a>
      </div>
    </div>
  );
}
